import React, { useEffect, useState } from 'react';
import {
  TrendingUp, Target, BarChart2, MessageSquare, Zap, Users,
  ArrowRight, CheckCircle2, Star, Eye, Heart, Share2
} from 'lucide-react';
import SEO from './SEO';

// Custom inline brand icons (lucide-react v1.x removed brand icons)
const Instagram = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);
const Youtube = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.495 6.205a3.007 3.007 0 00-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 00.527 6.205a31.247 31.247 0 00-.522 5.805 31.247 31.247 0 00.522 5.783 3.007 3.007 0 002.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 002.088-2.088 31.247 31.247 0 00.5-5.783 31.247 31.247 0 00-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/>
  </svg>
);
const Linkedin = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);
const Twitter = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const SocialMediaMarketing = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('instagram');

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsVisible(true);
  }, []);

  const platforms = [
    { id: 'instagram', label: 'Instagram', icon: Instagram, color: '#E1306C', bg: 'from-pink-600 to-orange-500' },
    { id: 'youtube', label: 'YouTube', icon: Youtube, color: '#FF0000', bg: 'from-red-600 to-red-400' },
    { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: '#0A66C2', bg: 'from-blue-700 to-blue-500' },
    { id: 'twitter', label: 'Twitter/X', icon: Twitter, color: '#1DA1F2', bg: 'from-sky-500 to-sky-400' },
  ];

  const platformContent = {
    instagram: {
      title: 'Instagram Growth Engine',
      desc: "We craft scroll-stopping Reels, Story campaigns, and carousel content that grow your following and turn viewers into paying customers. Our data-backed creative strategy ensures every post serves a purpose.",
      stats: [{ label: 'Avg. Reach Growth', val: '+340%' }, { label: 'Engagement Rate', val: '6.8%' }, { label: 'Follower Growth/mo', val: '+2.4K' }],
    },
    youtube: {
      title: 'YouTube Authority Building',
      desc: "From scripting to SEO-optimized thumbnails, we handle complete YouTube channel management. We grow your subscriber base with high-retention video content that builds brand authority and generates passive leads.",
      stats: [{ label: 'Watch Time Growth', val: '+280%' }, { label: 'Subscriber Growth', val: '+1.8K/mo' }, { label: 'Click-Through Rate', val: '9.2%' }],
    },
    linkedin: {
      title: 'LinkedIn B2B Lead Machine',
      desc: "We build your personal brand and company presence on LinkedIn through thought-leadership posts, strategic outreach, and targeted campaigns that generate high-quality B2B leads every single week.",
      stats: [{ label: 'Leads/Month', val: '+120' }, { label: 'Profile Views', val: '+540%' }, { label: 'Connection Growth', val: '+900/mo' }],
    },
    twitter: {
      title: 'Twitter/X Viral Strategy',
      desc: "We create high-engagement threads, viral hooks, and trending content strategies that grow your X presence rapidly. Perfect for building real-time brand relevance and thought leadership.",
      stats: [{ label: 'Impressions/mo', val: '2M+' }, { label: 'Follower Growth', val: '+3.2K/mo' }, { label: 'Engagement Rate', val: '4.5%' }],
    },
  };

  const services = [
    { icon: Target, title: 'Social Media Strategy', desc: 'Custom-built roadmaps aligned to your business goals — platform selection, content pillars, and growth KPIs.', color: 'text-[#0EA5E9]', bg: 'bg-[#0EA5E9]/10', border: 'border-[#0EA5E9]/20', hover: 'group-hover:bg-[#0EA5E9]' },
    { icon: Zap, title: 'Content Creation', desc: 'Professional graphics, Reels, video edits, and copywriting that stop the scroll and drive massive engagement.', color: 'text-[#0EA5E9]', bg: 'bg-[#0EA5E9]/10', border: 'border-[#0EA5E9]/20', hover: 'group-hover:bg-[#0EA5E9]' },
    { icon: BarChart2, title: 'Paid Social Ads', desc: 'ROI-positive ad campaigns on Meta, TikTok, and LinkedIn — precision targeting to reach your exact ideal customer.', color: 'text-[#0EA5E9]', bg: 'bg-[#0EA5E9]/10', border: 'border-[#0EA5E9]/20', hover: 'group-hover:bg-[#0EA5E9]' },
    { icon: Users, title: 'Community Management', desc: 'Daily DM responses, comment moderation, and community engagement to turn followers into brand advocates.', color: 'text-[#0EA5E9]', bg: 'bg-[#0EA5E9]/10', border: 'border-[#0EA5E9]/20', hover: 'group-hover:bg-[#0EA5E9]' },
    { icon: TrendingUp, title: 'Influencer Outreach', desc: 'Partnerships with micro and macro influencers in your niche to amplify your brand reach authentically.', color: 'text-[#0EA5E9]', bg: 'bg-[#0EA5E9]/10', border: 'border-[#0EA5E9]/20', hover: 'group-hover:bg-[#0EA5E9]' },
    { icon: MessageSquare, title: 'Analytics & Reporting', desc: 'Monthly performance dashboards tracking reach, leads, and ROI so you know exactly where every rupee is going.', color: 'text-[#0EA5E9]', bg: 'bg-[#0EA5E9]/10', border: 'border-[#0EA5E9]/20', hover: 'group-hover:bg-[#0EA5E9]' },
  ];

  const activePlatform = platformContent[activeTab];

  return (
    <div className="bg-[#050810] min-h-screen text-white overflow-hidden selection:bg-[#0EA5E9]/30" style={{ fontFamily: 'emoji' }}>
      <SEO
        title="Social Media Marketing Services – Vishwa DigiTech"
        description="Grow your brand with Vishwa DigiTech's social media marketing services. Expert management of Instagram, Facebook, YouTube, LinkedIn & Twitter/X. Increase followers, engagement & sales."
        keywords="social media marketing india, instagram marketing sangli, facebook ads management india, youtube marketing, linkedin marketing, social media agency maharashtra, SMM services india"
        canonicalPath="/services/social-media-marketing"
      />

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-float { animation: float 7s ease-in-out infinite; }
        .animate-float-delayed { animation: float-delayed 6s ease-in-out 2s infinite; }
        .animate-spin-slow { animation: spin-slow 20s linear infinite; }
        .ticker-track { animation: ticker 25s linear infinite; }
        
        .glass-card {
          background: rgba(255, 255, 255, 0.02);
          backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.06);
          transition: all 0.4s ease;
        }
        .glass-card:hover {
          background: rgba(14, 165, 233, 0.04);
          border-color: rgba(14, 165, 233, 0.3);
          transform: translateY(-6px);
          box-shadow: 0 20px 50px -10px rgba(14, 165, 233, 0.2);
        }
        .tab-active {
          background: linear-gradient(135deg, rgba(14,165,233,0.15), rgba(26,74,139,0.25));
          border-color: rgba(14, 165, 233, 0.5);
        }
      `}</style>

      {/* Background Orbs */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-10%] left-[-5%] w-[600px] h-[600px] bg-[#0EA5E9]/10 rounded-full blur-[180px] animate-float" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[500px] h-[500px] bg-[#1A4A8B]/20 rounded-full blur-[150px] animate-float-delayed" />
        <div className="absolute top-[45%] left-[45%] w-[300px] h-[300px] bg-[#0EA5E9]/5 rounded-full blur-[120px] animate-float" />
        {/* Grid pattern like AboutUs */}
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='60' height='60' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 60 0 L 0 0 0 60' fill='none' stroke='rgba(14,165,233,0.1)' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)'/%3E%3C/svg%3E")` }} />
      </div>

      <div className="relative z-10">

        {/* HERO SECTION */}
        <section className="pt-32 pb-24 px-4 max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-16">

          {/* Left Text */}
          <div className={`lg:w-1/2 transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0EA5E9]/10 border border-[#0EA5E9]/30 mb-8">
              <Share2 className="w-4 h-4 text-[#0EA5E9]" />
              <span className="text-sm font-bold tracking-widest text-[#0EA5E9] uppercase">Social Media Marketing</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-[1.1] mb-8 text-white">
              Turn Followers<br />Into <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-[#0EA5E9]">Loyal Customers.</span>
            </h1>

            <p className="text-lg md:text-xl text-gray-400 font-light mb-10 leading-relaxed max-w-xl">
              We build, grow, and monetize your social media presence across every major platform — creating genuine communities that drive consistent, predictable business growth.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <button className="px-8 py-4 bg-gradient-to-r from-[#0EA5E9] to-[#1A4A8B] text-white rounded-xl font-bold text-lg hover:scale-105 transition-all shadow-[0_0_30px_rgba(14,165,233,0.4)] flex items-center justify-center gap-3 group">
                Get Free SMM Audit <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button className="px-8 py-4 bg-white/5 border border-white/10 text-white rounded-xl font-medium text-lg hover:bg-white/10 transition-colors flex items-center justify-center gap-3">
                View Case Studies
              </button>
            </div>

            <div className="flex items-center gap-8 text-sm text-gray-500 font-medium">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#0EA5E9]" />Free Strategy Call</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#0EA5E9]" />No Long-Term Lock-in</div>
            </div>
          </div>

          {/* Right: Social Card Stack */}
          <div className={`lg:w-1/2 w-full flex justify-center items-center relative h-[420px] transition-all duration-1000 delay-300 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            {/* Central Rotating Ring */}
            <div className="absolute w-72 h-72 rounded-full border border-[#0EA5E9]/15 animate-spin-slow"></div>
            <div className="absolute w-52 h-52 rounded-full border border-[#1A4A8B]/30 animate-spin-slow" style={{ animationDirection: 'reverse', animationDuration: '15s' }}></div>

            {/* Center Circle */}
            <div className="w-28 h-28 rounded-full bg-gradient-to-br from-[#0EA5E9] to-[#1A4A8B] flex items-center justify-center shadow-[0_0_60px_rgba(14,165,233,0.5)] z-10">
              <TrendingUp className="w-12 h-12 text-white" />
            </div>

            {/* Orbiting Social Icons */}
            <div className="absolute top-8 left-[calc(50%-70px)] glass-card p-3 rounded-2xl shadow-xl animate-float z-10">
              <Instagram className="w-8 h-8 text-[#0EA5E9]" />
            </div>
            <div className="absolute bottom-12 left-12 glass-card p-3 rounded-2xl shadow-xl animate-float-delayed z-10">
              <Youtube className="w-8 h-8 text-[#0EA5E9]" />
            </div>
            <div className="absolute top-16 right-10 glass-card p-3 rounded-2xl shadow-xl animate-float-delayed z-10">
              <Linkedin className="w-8 h-8 text-[#0EA5E9]" />
            </div>
            <div className="absolute bottom-8 right-[calc(50%-50px)] glass-card p-3 rounded-2xl shadow-xl animate-float z-10">
              <Twitter className="w-8 h-8 text-[#0EA5E9]" />
            </div>

            {/* Stats Floating Cards */}
            <div className="absolute top-4 right-4 glass-card px-4 py-2 rounded-xl z-20 animate-float-delayed border border-[#0EA5E9]/20">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-[#0EA5E9]" />
                <div>
                  <p className="text-xs text-gray-400">Reach</p>
                  <p className="font-bold text-white text-sm">+2.4M</p>
                </div>
              </div>
            </div>

            <div className="absolute bottom-4 left-4 glass-card px-4 py-2 rounded-xl z-20 animate-float border border-[#0EA5E9]/20">
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-[#0EA5E9]" />
                <div>
                  <p className="text-xs text-gray-400">Engagement</p>
                  <p className="font-bold text-white text-sm">7.8%</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* STATS TICKER */}
        <section className="py-6 border-y border-white/5 overflow-hidden bg-white/[0.01] relative">
          <div className="flex ticker-track whitespace-nowrap gap-12">
            {[...Array(2)].map((_, i) =>
              ['500+ Clients Served', '📈 Avg. 340% Reach Growth', '⭐ 4.9/5 Client Rating', '🚀 10M+ Monthly Impressions Managed', '💰 ₹5Cr+ Ad Spend Managed', '🎯 98% Client Retention', '✅ Green-Tick Verified Campaigns'].map((text, idx) => (
                <span key={`${i}-${idx}`} className="text-gray-400 font-medium text-sm uppercase tracking-wider">{text}&nbsp;&nbsp;&nbsp;<span className="text-[#0EA5E9]">✦</span>&nbsp;&nbsp;&nbsp;</span>
              ))
            )}
          </div>
        </section>

        {/* SERVICES GRID */}
        <section className="py-28 px-4 max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Everything You Need to <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Dominate</span> Social
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#0EA5E9] to-transparent mx-auto mb-6"></div>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto font-light">A full-stack social media solution — from strategy and content to ads and analytics.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, idx) => (
              <div key={idx} className="glass-card p-8 rounded-3xl relative overflow-hidden group">
                <div className={`absolute -right-10 -top-10 w-32 h-32 rounded-full blur-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-500 ${s.bg}`}></div>
                <div className={`w-14 h-14 ${s.bg} ${s.border} border ${s.color} rounded-2xl flex items-center justify-center mb-6 ${s.hover} group-hover:text-white transition-all duration-500 shadow-lg relative z-10`}>
                  <s.icon className="w-7 h-7" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3 relative z-10">{s.title}</h3>
                <p className="text-gray-400 leading-relaxed relative z-10">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* PLATFORM TABS SECTION */}
        <section className="py-28 px-4 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Platform-Specific <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Mastery</span></h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#0EA5E9] to-transparent mx-auto mb-6"></div>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto font-light">Each platform has its own algorithm, culture, and audience. We speak every language fluently.</p>
          </div>

          {/* Tab Buttons */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {platforms.map(p => (
              <button
                key={p.id}
                onClick={() => setActiveTab(p.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full border font-semibold text-sm transition-all duration-300 ${activeTab === p.id ? 'tab-active text-white border-[#0EA5E9]/50 shadow-[0_0_20px_rgba(14,165,233,0.2)]' : 'bg-white/5 border-white/10 text-gray-400 hover:text-white hover:bg-white/10'}`}
              >
                <p.icon className="w-4 h-4" style={{ color: activeTab === p.id ? p.color : undefined }} />
                {p.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div key={activeTab} className="bg-[#0A0F1F] border border-white/10 rounded-[2.5rem] p-8 md:p-12 flex flex-col md:flex-row items-center gap-12 transition-all duration-300 shadow-2xl">
            <div className="flex-1">
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-5">{activePlatform.title}</h3>
              <p className="text-lg text-gray-400 leading-relaxed mb-8">{activePlatform.desc}</p>
              <button className="px-8 py-3 bg-gradient-to-r from-[#0EA5E9] to-[#1A4A8B] text-white rounded-xl font-semibold hover:scale-105 transition-all shadow-[0_0_20px_rgba(14,165,233,0.3)] flex items-center gap-2 group">
                Get Started <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
            <div className="flex-1 grid grid-cols-3 gap-4 w-full">
              {activePlatform.stats.map((stat, i) => (
                <div key={i} className="text-center p-4 bg-white/5 border border-white/5 rounded-2xl hover:border-[#0EA5E9]/20 transition-colors">
                  <h4 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-b from-white to-[#0EA5E9] mb-2">{stat.val}</h4>
                  <p className="text-gray-400 text-xs font-medium leading-tight">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 px-4 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Clients Who <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Love</span> Results</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-[#0EA5E9] to-transparent mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "Priya Sharma", role: "Founder, StyleBoutique", review: "Our Instagram went from 2K to 18K followers in just 4 months. Sales through social media tripled. Absolutely incredible results!", rating: 5 },
              { name: "Arjun Mehta", role: "CEO, TechStartup", review: "The LinkedIn strategy brought us over 50 qualified B2B leads per month. The ROI is phenomenal compared to any other channel.", rating: 5 },
              { name: "Sneha Patel", role: "Marketing Head, RetailBrand", review: "Their Reels content strategy is pure gold. We consistently hit 100K+ views and our DMs are filled with customer queries daily.", rating: 5 },
            ].map((t, i) => (
              <div key={i} className="glass-card p-8 rounded-3xl relative">
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, s) => <Star key={s} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                </div>
                <p className="text-gray-300 leading-relaxed mb-6 italic">"{t.review}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#0EA5E9] to-[#1A4A8B] rounded-full flex items-center justify-center font-bold text-white">{t.name[0]}</div>
                  <div>
                    <h5 className="font-bold text-white text-sm">{t.name}</h5>
                    <p className="text-gray-500 text-xs">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>


      </div>
    </div>
  );
};

export default SocialMediaMarketing;
